import { Component } from '@verndale/core';

class IconBannerReskin extends Component {
  dom!: {
    track: HTMLElement | null;
    items: HTMLElement[];
    pagination: HTMLElement | null;
    dots: HTMLButtonElement[];
  };

  activeIndex = 0;
  resizeObserver: ResizeObserver | null = null;
  boundOnScroll: (() => void) | null = null;
  boundOnResize: (() => void) | null = null;

  constructor(el: HTMLElement) {
    super(el);
    this.init();
  }

  setupDefaults() {
    const track = this.el.querySelector<HTMLElement>('.container');
    const items = Array.from(this.el.querySelectorAll<HTMLElement>('.icon-banner-reskin__item'));
    const pagination = this.el.querySelector<HTMLElement>('[data-icon-banner-pagination]');

    this.dom = {
      track,
      items,
      pagination,
      dots: []
    };
  }

  isMobile() {
    return window.innerWidth < 768;
  }

  buildPagination() {
    const { pagination, items } = this.dom;

    if (!pagination) return;

    pagination.innerHTML = '';
    this.dom.dots = [];

    items.forEach((_, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'icon-banner-reskin__pagination-dot';
      dot.setAttribute('aria-label', `Go to slide ${index + 1}`);

      dot.addEventListener('click', () => {
        this.scrollToIndex(index);
      });

      pagination.appendChild(dot);
      this.dom.dots.push(dot);
    });

    this.updatePagination();
  }

  getItemOffset(index: number) {
    const { track, items } = this.dom;
    if (!track || !items[index]) return 0;

    const item = items[index];
    return item.offsetLeft - track.offsetLeft;
  }

  scrollToIndex(index: number) {
    const { track } = this.dom;
    if (!track) return;

    const left = this.getItemOffset(index);

    track.scrollTo({
      left,
      behavior: 'smooth'
    });

    this.activeIndex = index;
    this.updatePagination();
  }

  getActiveIndex() {
    const { track, items } = this.dom;
    if (!track || !items.length) return 0;

    const trackScrollLeft = track.scrollLeft;
    const trackCenter = trackScrollLeft + track.clientWidth / 2;

    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    items.forEach((item, index) => {
      const itemCenter = item.offsetLeft - track.offsetLeft + item.offsetWidth / 2;
      const distance = Math.abs(trackCenter - itemCenter);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    return closestIndex;
  }

  updatePagination() {
    this.dom.dots.forEach((dot, index) => {
      const isActive = index === this.activeIndex;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  handleScroll() {
    if (!this.isMobile()) return;

    const nextIndex = this.getActiveIndex();

    if (nextIndex !== this.activeIndex) {
      this.activeIndex = nextIndex;
      this.updatePagination();
    }
  }

  handleResize() {
    if (!this.dom.track) return;

    if (!this.isMobile()) {
      this.activeIndex = 0;
      this.updatePagination();
      return;
    }

    this.activeIndex = this.getActiveIndex();
    this.updatePagination();
  }

  addListeners() {
    const { track } = this.dom;
    if (!track) return;

    this.boundOnScroll = this.handleScroll.bind(this);
    this.boundOnResize = this.handleResize.bind(this);

    track.addEventListener('scroll', this.boundOnScroll, { passive: true });
    window.addEventListener('resize', this.boundOnResize);

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => {
        this.handleResize();
      });

      this.resizeObserver.observe(track);
    }
  }

  removeListeners() {
    const { track } = this.dom;

    if (track && this.boundOnScroll) {
      track.removeEventListener('scroll', this.boundOnScroll);
    }

    if (this.boundOnResize) {
      window.removeEventListener('resize', this.boundOnResize);
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  init() {
    this.setupDefaults();
    this.buildPagination();
    this.addListeners();
    this.handleResize();
  }

  destroy() {
    this.removeListeners();
  }
}

export default IconBannerReskin;